<template>
  <q-card flat style="max-width: 450px">
    <q-card-section>
      <div class="text-h6">Similarity Breakdown</div>
      <div class="text-caption text-grey-7">How similar is this comp to the subject?</div>
    </q-card-section>
    <q-markup-table flat dense square>
      <thead>
        <tr class="text-left text-grey-7">
          <th scope="col" class="text-left">Dimension</th>
          <th scope="col" class="text-right">Similarity</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="simD in displayedSimilarityDescriptions" :key="simD.key">
          <td class="text-left" :class="{ 'text-bold': simD.key === 'overall' }">
            {{ simD.name }}
            <q-icon name="help" size="14px" class="text-grey-7">
              <q-tooltip>
                <div>{{ simD.description }}</div>
              </q-tooltip>
            </q-icon>
          </td>
          <td class="text-right" :class="{ 'text-bold': simD.key === 'overall' }">
            <q-avatar
              rounded
              size="22px"
              :style="{ backgroundColor: similarityColor(simD.getter(similarities)) }"
              text-color="white"
            >
              <template v-if="simD.getter(similarities) !== null">
                {{ formatNumber(simD.getter(similarities)! * 100) }}
              </template>
              <template v-else> N/A </template>
            </q-avatar>
          </td>
        </tr>
      </tbody>
    </q-markup-table>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'

import type { Similarities } from 'common'
import { formatNumber, formatPercent, similarityColor } from '@/utils'

export default {
  name: 'SimilarityCard',
  props: {
    similarities: {
      required: true,
      type: Object as PropType<Similarities>
    },
    hiddenDimensions: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },
  data() {
    return {
      similarityColor,
      formatPercent,
      formatNumber,
      similiarityDescriptions: [
        {
          key: 'overall',
          description:
            'Overall similarity between this property and the subject property. This is the average of all the other similarity scores.',
          name: 'Overall',
          level: 'overall',
          getter: (sim: Similarities) => {
            return sim.overall
          }
        },
        {
          key: 'market',
          description:
            "Similarity of demographics and assets between this property's market and the subject property's market.",
          name: 'Submarket',
          level: 'location',
          getter: (sim: Similarities) => {
            return sim.market
          }
        },
        {
          key: 'distance',
          description:
            'Distance between this property and the subject property. The score drops faster in high-density areas than slower in low-density areas.',
          name: 'Distance',
          level: 'location',
          getter: (sim: Similarities) => {
            return sim.distance
          }
        },
        {
          key: 'number_units',
          description:
            'Similarity of number of units between this property and the subject property.',
          name: 'Number of Units',
          level: 'building',
          getter: (sim: Similarities) => {
            return sim.number_units
          }
        },
        {
          key: 'vintage',
          description: 'Similarity of vintage between this property and the subject property.',
          name: 'Vintage',
          level: 'building',
          getter: (sim: Similarities) => {
            return sim.vintage
          }
        },
        {
          key: 'number_stories',
          description:
            'Similarity of number of stories between this property and the subject property.',
          name: 'Number of Stories',
          level: 'building',
          getter: (sim: Similarities) => {
            return sim.number_stories
          }
        },
        {
          key: 'unit_mix',
          description:
            'Maximum similarity of unit types between this property and the subject property, using their avg sqft',
          name: 'Unit Mix',
          level: 'unit',
          getter: (sim: Similarities) => {
            if (sim.unit_mix === null || Object.keys(sim.unit_mix).length === 0) {
              return null
            }
            // compute the avg similarity
            return (
              Object.values(sim.unit_mix).reduce((acc, val) => acc + val, 0) /
              Object.keys(sim.unit_mix).length
            )
          }
        },
        {
          key: 'quality',
          description:
            'Similarity of quality between this property and the subject property. Estimated by our proprietary AI.',
          name: 'Quality',
          level: 'building',
          getter: (sim: Similarities) => {
            return sim.quality
          }
        },
        {
          key: 'building_amenities',
          description:
            'Overlap between most important building amenities for this property and the subject property.',
          name: 'Building Amenities',
          level: 'building',
          getter: (sim: Similarities) => {
            return sim.building_amenities
          }
        },
        {
          key: 'unit_amenities',
          description:
            'Overlap between most important unit amenities for this property and the subject property.',
          name: 'Unit Amenities',
          level: 'unit',
          getter: (sim: Similarities) => {
            return sim.unit_amenities
          }
        },
        {
          key: 'distances_to_universities',
          description:
            'Similarity in distance to a universities, if property is closer to a different university than the subject property then similarity is discounted.',
          name: 'Distance to Campus',
          level: 'building',
          getter: (sim: Similarities) => {
            return sim.distances_to_universities
          }
        },
        {
          key: 'subtype',
          description:
            'Similarity of asset class between this property and the subject property. Using detected senior housing for now.',
          name: 'Asset Class',
          level: 'building',
          getter: (sim: Similarities) => {
            return sim.subtype
          }
        }
      ]
    }
  },
  computed: {
    displayedSimilarityDescriptions() {
      return this.similiarityDescriptions.filter(
        (simD) => !this.hiddenDimensions.includes(simD.key)
      )
    }
  }
}
</script>
